import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const STATUS_LABEL = {
  approved: "Approved", processing: "Processing", paid: "Paid",
  reversed: "Reversed", clawback: "Clawback",
};

/**
 * Read-only ledger for partners. Backend filters rows to
 * approved/processing/paid/reversed/clawback for the caller's partner code,
 * and strips finance-only notes. Displays a per-row PDF and an
 * aggregate PDF.
 */
export default function MyPartnerEarnings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/partner-earnings");
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load earnings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const t = { gross: 0, net: 0, tds: 0 };
    rows.forEach(r => {
      t.gross += r.gross_amount || 0;
      t.net += r.net_payable || 0;
      t.tds += r.tds || 0;
    });
    return t;
  }, [rows]);

  const partnerCode = rows[0]?.partner_code;

  const downloadOne = async (eid) => {
    try {
      const r = await api.get(`/partner-earnings/${eid}/statement.pdf`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `miro_payout_${eid.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    }
  };

  const downloadBulk = async () => {
    if (!partnerCode) return;
    try {
      const r = await api.get(`/partner-earnings/statement.pdf`,
        { params: { partner_code: partnerCode }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `miro_payout_${partnerCode}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk PDF download failed");
    }
  };

  return (
    <AppLayout>
      <div className="p-6" data-testid="my-partner-earnings">
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="miro-overline">Payouts</div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
              My Earnings
            </h1>
            <p className="text-sm text-[var(--miro-text-muted)] mt-1">
              Approved and paid entries only. Numbers are recorded manually by
              MIRO Finance.
            </p>
          </div>
          {partnerCode && (
            <Button variant="outline" onClick={downloadBulk}
              data-testid="my-bulk-pdf">
              <Download size={14} className="mr-1"/> Download consolidated PDF
            </Button>
          )}
        </div>

        {loading && <div className="p-6 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="bg-white border rounded-xl p-8 text-center">
            <p className="text-sm text-[var(--miro-text-muted)]">
              No approved earnings yet. When MIRO Finance approves your first
              entry, it will appear here.
            </p>
          </div>
        )}

        {rows.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-white border rounded-xl p-4">
                <div className="text-xs text-[var(--miro-text-muted)]">Gross earned</div>
                <div className="text-2xl font-black">{totals.gross.toFixed(0)}</div>
              </div>
              <div className="bg-white border rounded-xl p-4">
                <div className="text-xs text-[var(--miro-text-muted)]">TDS</div>
                <div className="text-2xl font-black">{totals.tds.toFixed(0)}</div>
              </div>
              <div className="bg-white border rounded-xl p-4">
                <div className="text-xs text-[var(--miro-text-muted)]">Net payable</div>
                <div className="text-2xl font-black text-emerald-700">{totals.net.toFixed(0)}</div>
              </div>
            </div>

            <div className="bg-white border rounded-xl overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-widest text-[var(--miro-text-muted)] border-b bg-slate-50">
                <div className="col-span-2">Date</div>
                <div className="col-span-2">Product</div>
                <div>Gross</div><div>TDS</div><div>Net</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Ref</div>
                <div className="text-right">PDF</div>
              </div>
              {rows.map(r => (
                <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b text-sm items-center"
                     data-testid={`my-earning-row-${r.id}`}>
                  <div className="col-span-2 text-xs">
                    {(r.payout_date || r.created_at || "").slice(0, 10)}
                  </div>
                  <div className="col-span-2 text-xs">{r.product}</div>
                  <div>{Number(r.gross_amount || 0).toFixed(0)}</div>
                  <div>{Number(r.tds || 0).toFixed(0)}</div>
                  <div className="font-bold">{Number(r.net_payable || 0).toFixed(0)}</div>
                  <div className="col-span-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100">
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                  <div className="col-span-2 text-xs font-mono truncate">
                    {r.payment_reference || "—"}
                  </div>
                  <div className="text-right">
                    <Button size="sm" variant="ghost" className="h-6 px-2"
                      onClick={() => downloadOne(r.id)}
                      data-testid={`my-pdf-${r.id}`}>
                      <Download size={12}/>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
